import React, { useEffect, useState } from "react";
import { MessageSquare, X, Target, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const MAX_QTY = 200;

function QuantityStepper({ value, onChange }) {
  const set = (v) => onChange(Math.max(1, Math.min(MAX_QTY, Number.isFinite(v) ? v : 1)));
  return (
    <div className="flex items-center gap-2">
      <button type="button" data-testid="lead-qty-decrement-btn" onClick={() => set(value - 1)}
        className="w-9 h-9 rounded-lg bg-[#1F2330] border border-[#3E4760] text-white flex items-center justify-center hover:border-[#FF6B00] transition-colors">
        <Minus className="w-4 h-4" />
      </button>
      <input data-testid="lead-qty-input" inputMode="numeric" value={value}
        onChange={(e) => set(parseInt(e.target.value.replace(/\D/g, ""), 10))}
        className="w-16 h-9 text-center bg-[#0F1218] border border-[#282D3D] rounded-lg text-white font-extrabold focus:outline-none focus:border-[#FF6B00]" />
      <button type="button" data-testid="lead-qty-increment-btn" onClick={() => set(value + 1)}
        className="w-9 h-9 rounded-lg bg-[#1F2330] border border-[#3E4760] text-white flex items-center justify-center hover:border-[#FF6B00] transition-colors">
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

const STAGE_LABEL = {
  LIVE_PRETEST: "Pré-Teste",
  LIVE_TEST: "Teste",
  LIVE_PRESCALE: "Pré-Escala",
  LIVE_SCALE: "Escala",
};

export default function RegisterLeadModal({ isOpen, onClose, onLeadSuccess }) {
  const [creatives, setCreatives] = useState([]);
  const [creativeId, setCreativeId] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [cycleOptions, setCycleOptions] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [mode, setMode] = useState("add"); // add | reduce

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await api.get("/api/creatives/");
        const list = res.data?.creatives || [];
        setCreatives(list);
        if (list.length && !creativeId) setCreativeId(list[0].id || list[0]._id);
      } catch (_err) { /* ignore */ }
    })();
    setError("");
    setCampaignOptions([]);
    setCampaignId("");
    setCycleOptions([]);
    setCycleId("");
    setIdentifier("");
    setQuantity(1);
    setMode("add");
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!creativeId) return;
    (async () => {
      try {
        const res = await api.get(`/api/leads/campaigns-for-creative/${creativeId}`);
        const camps = res.data?.campaigns || [];
        setCampaignOptions(camps);
        setCampaignId(camps.length === 1 ? camps[0].id : "");
      } catch (_err) { /* ignore */ }
    })();
  }, [creativeId]);

  if (!isOpen) return null;

  const handleResponseError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object") {
      if (detail.code === "campaign_ambiguous") {
        setCampaignOptions(detail.campaigns || []);
        setError("Este criativo tem mais de uma campanha. Selecione a campanha.");
        return true;
      }
      if (detail.code === "cycle_ambiguous") {
        setCycleOptions(detail.cycles || []);
        setError("Você tem mais de um ciclo Live ativo. Em qual operação?");
        return true;
      }
      if (detail.code === "no_active_cycle") {
        setError(detail.message || "Nenhum ciclo Live ativo. Inicie um ciclo primeiro.");
        return true;
      }
      setError(detail.message || "Erro ao registrar lead.");
      return true;
    }
    setError(typeof detail === "string" ? detail : "Erro ao registrar lead.");
    return true;
  };

  const submitLead = async () => {
    setError("");
    if (!creativeId) {
      setError("Selecione o criativo.");
      return;
    }
    setLoading(true);
    try {
      const body = { creative_id: creativeId, reached_values: false, quantity };
      if (identifier.trim()) body.identifier = identifier.trim();
      if (campaignOptions.length > 1 && campaignId) body.campaign_id = campaignId;
      if (cycleId) body.cycle_id = cycleId;
      await api.post("/api/leads", body);
      onLeadSuccess?.();
      onClose();
    } catch (err) {
      handleResponseError(err);
    } finally {
      setLoading(false);
    }
  };

  const qualifyValores = async () => {
    setError("");
    if (!creativeId) {
      setError("Selecione o criativo.");
      return;
    }
    setLoading(true);
    try {
      const body = { creative_id: creativeId, quantity };
      if (campaignOptions.length > 1 && campaignId) body.campaign_id = campaignId;
      if (cycleId) body.cycle_id = cycleId;
      const res = await api.post("/api/leads/qualify-reached-values", body);
      if (res.data?.created_qualified) {
        toast.info(`${res.data.created_qualified} lead(s) criado(s) automaticamente já em Valores.`);
      }
      if (res.data?.advanced_stage) toast.success("Ciclo avançou para Teste!");
      onLeadSuccess?.();
      onClose();
    } catch (err) {
      handleResponseError(err);
    } finally {
      setLoading(false);
    }
  };

  const reduce = async (kind) => {
    setError("");
    if (!creativeId) {
      setError("Selecione o criativo.");
      return;
    }
    setLoading(true);
    try {
      const body = { creative_id: creativeId, quantity, kind };
      if (campaignOptions.length > 1 && campaignId) body.campaign_id = campaignId;
      if (cycleId) body.cycle_id = cycleId;
      const res = await api.post("/api/leads/reduce", body);
      toast.success(kind === "leads" ? `${quantity} lead(s) removido(s).` : `${quantity} lead(s) retirado(s) de Valores.`);
      if (res.data?.regressed_stage) toast.warning("O ciclo voltou para Pré-Teste (menos de 3 em Valores).");
      onLeadSuccess?.();
      onClose();
    } catch (err) {
      handleResponseError(err);
    } finally {
      setLoading(false);
    }
  };

  const isReduce = mode === "reduce";

  return (
    <div
      data-testid="register-lead-modal-overlay"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        data-testid="register-lead-modal"
        className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-md p-6 relative shadow-2xl"
      >
        <button
          data-testid="close-lead-modal-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-[#FF6B00]" />
          <h2 className="text-lg font-extrabold text-white">{isReduce ? "Corrigir Leads" : "Registrar Lead"}</h2>
        </div>

        <div className="flex bg-[#0F1218] border border-[#282D3D] rounded-lg p-1 mb-4">
          {[["add", "Adicionar"], ["reduce", "Corrigir / Reduzir"]].map(([k, label]) => (
            <button key={k} type="button" data-testid={`lead-mode-${k}-btn`} onClick={() => { setMode(k); setError(""); }}
              className={`flex-1 py-1.5 rounded-md text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                mode === k ? (k === "reduce" ? "bg-red-500/20 text-red-300" : "bg-[#FF6B00] text-black") : "text-gray-400 hover:text-white"
              }`}>
              {label}
            </button>
          ))}
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">Criativo *</label>
            <select
              data-testid="lead-creative-select"
              value={creativeId}
              onChange={(e) => setCreativeId(e.target.value)}
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            >
              <option value="">— selecione —</option>
              {creatives.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          {campaignOptions.length > 1 && (
            <div>
              <label className="block text-[11px] font-bold text-gray-300 mb-1">Campanha</label>
              <select
                data-testid="lead-campaign-select"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
              >
                <option value="">— selecione —</option>
                {campaignOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.stage})</option>
                ))}
              </select>
            </div>
          )}

          {cycleOptions.length > 1 && (
            <div>
              <label className="block text-[11px] font-bold text-amber-300 mb-1">
                Em qual operação? *
              </label>
              <select
                data-testid="lead-cycle-select"
                value={cycleId}
                onChange={(e) => setCycleId(e.target.value)}
                className="w-full bg-[#0F1218] border border-amber-500/40 rounded-lg px-2 py-2 text-white"
              >
                <option value="">— selecione o ciclo —</option>
                {cycleOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || `Ciclo #${c.cycle_number}`} — {STAGE_LABEL[c.stage] || c.stage}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Quantidade {isReduce ? "a remover" : ""}
            </label>
            <QuantityStepper value={quantity} onChange={setQuantity} />
            <p className="text-[10px] text-gray-500 mt-1">
              {isReduce
                ? "Use para corrigir lançamentos feitos por engano. Se Valores ficar abaixo de 3, o ciclo volta para Pré-Teste."
                : "Registrou depois? Informe quantos leads chegaram de uma vez."}
            </p>
          </div>

          {!isReduce && (
          <div>
            <label className="block text-[11px] font-bold text-gray-300 mb-1">
              Nome ou telefone <span className="text-gray-500">(opcional)</span>
            </label>
            <input
              data-testid="lead-identifier-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Nome, telefone ou referência"
              className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white"
            />
          </div>
          )}

          {error && <div data-testid="lead-error" className="text-xs text-red-400">{error}</div>}
        </div>

        {isReduce ? (
        <div className="flex gap-2 mt-5">
          <button data-testid="reduce-leads-btn" onClick={() => reduce("leads")} disabled={loading}
            className="flex-1 py-2 rounded-lg bg-[#1F2330] hover:bg-red-500/20 border border-red-500/40 text-red-200 font-extrabold text-xs uppercase flex items-center justify-center gap-1 disabled:opacity-60">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `− ${quantity} Lead${quantity > 1 ? "s" : ""}`}
          </button>
          <button data-testid="reduce-reached-values-btn" onClick={() => reduce("reached_values")} disabled={loading}
            className="flex-1 py-2 rounded-lg bg-[#1F2330] hover:bg-red-500/20 border border-red-500/40 text-red-200 font-extrabold text-xs uppercase flex items-center justify-center gap-1 disabled:opacity-60">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `− ${quantity} em Valores`}
          </button>
        </div>
        ) : (
        <div className="flex gap-2 mt-5">
          <button
            data-testid="submit-lead-btn"
            onClick={submitLead}
            disabled={loading}
            className="flex-1 py-2 rounded-lg bg-[#1F2330] hover:bg-[#2A3041] border border-[#3E4760] text-white font-extrabold text-xs uppercase flex items-center justify-center gap-1 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `+ ${quantity} Lead${quantity > 1 ? "s" : ""}`}
          </button>
          <button
            data-testid="submit-lead-reached-values-btn"
            onClick={qualifyValores}
            disabled={loading}
            title="Qualifica os leads pendentes mais antigos; cria os que faltarem já qualificados"
            className="flex-1 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase flex items-center justify-center gap-1 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><Target className="w-3.5 h-3.5" /> {quantity > 1 ? `${quantity} em Valores` : "Chegou em Valores"}</>)}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
